// src/pages/finance/FeeSetup.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeftIcon,
  BuildingLibraryIcon,
  CurrencyDollarIcon,
  PencilSquareIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";
import apiClient from "../../api/client";
import { FireLoader } from "../../components/Loader";
import { showError, showSuccess } from "../../utils/alert";

const arrayFrom = (response) => {
  const payload = response?.data?.data ?? response?.data ?? [];
  if (Array.isArray(payload)) return payload;
  return payload.rows || payload.items || payload.classes || payload.data || [];
};

const sle = (value) =>
  `SLE ${new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0))}`;

const SCHOOL_TYPE_LABELS = {
  government: "Government School",
  government_assisted: "Government-Assisted School",
  private: "Private School",
};

export default function FeeSetup() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [policySaving, setPolicySaving] = useState(false);
  const [classes, setClasses] = useState([]);
  const [years, setYears] = useState([]);
  const [terms, setTerms] = useState([]);
  const [fees, setFees] = useState([]);
  const [policy, setPolicy] = useState({
    school_type: "private",
    tuition_model: "fee_paying",
    is_free_education: false,
  });
  const [selectedSchoolType, setSelectedSchoolType] = useState("private");
  const [form, setForm] = useState({
    class_id: "",
    academic_year_id: "",
    academic_term_id: "",
    amount: "",
    due_date: "",
    notes: "",
  });

  const isFreeEducation = Boolean(policy.is_free_education);

  const visibleTerms = useMemo(
    () =>
      terms.filter(
        (term) =>
          !form.academic_year_id ||
          Number(term.academic_year_id) === Number(form.academic_year_id),
      ),
    [terms, form.academic_year_id],
  );

  const loadAll = useCallback(async () => {
    const [policyResponse, classResponse, yearResponse, termResponse, feeResponse] =
      await Promise.all([
        apiClient.get("/finance/policy"),
        apiClient.get("/classes", { params: { page: 1, limit: 1000 } }),
        apiClient.get("/academic-years"),
        apiClient.get("/academic-terms"),
        apiClient.get("/finance/fees"),
      ]);

    const nextPolicy = policyResponse?.data?.data || {};
    const classList = arrayFrom(classResponse);
    const yearList = arrayFrom(yearResponse);
    const termList = arrayFrom(termResponse);

    setPolicy(nextPolicy);
    setSelectedSchoolType(nextPolicy.school_type || "private");
    setClasses(classList);
    setYears(yearList);
    setTerms(termList);
    setFees(arrayFrom(feeResponse));

    const currentYear = yearList.find((item) => item.is_current) || yearList[0];
    const currentTerm = currentYear
      ? termList.find(
          (item) =>
            Number(item.academic_year_id) === Number(currentYear.id) &&
            (item.is_current || item.status === "active"),
        )
      : null;

    setForm((previous) => ({
      ...previous,
      academic_year_id:
        previous.academic_year_id ||
        (currentYear ? String(currentYear.id) : ""),
      academic_term_id:
        previous.academic_term_id ||
        (currentTerm ? String(currentTerm.id) : ""),
    }));
  }, []);

  useEffect(() => {
    loadAll()
      .catch((error) => {
        console.error(error);
        showError({ text: "Failed to load fee setup information." });
      })
      .finally(() => setLoading(false));
  }, [loadAll]);

  const handlePolicySave = async () => {
    try {
      setPolicySaving(true);
      const response = await apiClient.put("/finance/policy", {
        school_type: selectedSchoolType,
      });
      const nextPolicy = response?.data?.data || {};
      setPolicy(nextPolicy);
      setSelectedSchoolType(nextPolicy.school_type || selectedSchoolType);
      await showSuccess({
        text:
          response?.data?.message ||
          "School tuition policy updated successfully.",
      });
    } catch (error) {
      console.error(error);
      await showError({
        text:
          error.response?.data?.message ||
          "Failed to update the school tuition policy.",
      });
    } finally {
      setPolicySaving(false);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((previous) => ({
      ...previous,
      [name]: value,
      ...(name === "academic_year_id"
        ? { academic_term_id: "" }
        : {}),
    }));
  };

  const editFee = (fee) => {
    setForm({
      class_id: String(fee.class_id),
      academic_year_id: String(fee.academic_year_id),
      academic_term_id: String(fee.academic_term_id),
      amount: String(fee.amount || ""),
      due_date: fee.due_date || "",
      notes: fee.notes || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isFreeEducation) {
      return showError({
        text: "Student tuition charging is disabled for a Free Education school.",
      });
    }

    if (
      !form.class_id ||
      !form.academic_year_id ||
      !form.academic_term_id ||
      Number(form.amount) <= 0
    ) {
      return showError({
        text:
          "Select a class, academic year, term and enter a valid fee amount.",
      });
    }

    try {
      setSaving(true);
      const response = await apiClient.post("/finance/fees", {
        ...form,
        class_id: Number(form.class_id),
        academic_year_id: Number(form.academic_year_id),
        academic_term_id: Number(form.academic_term_id),
        amount: form.amount,
      });

      await showSuccess({
        text:
          response?.data?.message ||
          "Class fee saved successfully.",
      });
      await loadAll();
      setForm((previous) => ({
        ...previous,
        class_id: "",
        amount: "",
        due_date: "",
        notes: "",
      }));
    } catch (error) {
      console.error(error);
      await showError({
        text:
          error.response?.data?.message ||
          "Failed to save class fee.",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <FireLoader
        fullScreen={true}
        size="lg"
        text="LOADING FINANCE POLICY..."
      />
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-5 p-4 sm:p-6 lg:p-8">
      <div className="flex items-center gap-3">
        <Link
          to="/app/finance"
          className="rounded-lg border border-slate-200 p-2 text-slate-500 hover:bg-slate-50"
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            School Tuition Setup
          </h1>
          <p className="text-sm text-slate-500">
            Configure whether the school uses Free Education or student-paid
            term fees.
          </p>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
            <BuildingLibraryIcon className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-sm font-bold text-slate-900">
              School Funding Type
            </h2>
            <p className="mt-1 text-xs leading-relaxed text-slate-500">
              Government and government-assisted schools are automatically
              placed under Free Education. Private schools can configure class
              fees for each academic term.
            </p>
          </div>
        </div>

        <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-end">
          <label className="flex-1 text-xs font-medium text-slate-600">
            School Type
            <select
              value={selectedSchoolType}
              onChange={(event) => setSelectedSchoolType(event.target.value)}
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-primary-400"
            >
              <option value="government">Government School</option>
              <option value="government_assisted">
                Government-Assisted School
              </option>
              <option value="private">Private School</option>
            </select>
          </label>

          <button
            type="button"
            disabled={
              policySaving ||
              selectedSchoolType === policy.school_type
            }
            onClick={handlePolicySave}
            className="rounded-lg bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {policySaving ? "Saving..." : "Save School Type"}
          </button>
        </div>

        <div
          className={`mt-4 rounded-xl border px-4 py-3 ${
            isFreeEducation
              ? "border-emerald-200 bg-emerald-50"
              : "border-blue-200 bg-blue-50"
          }`}
        >
          <div className="flex items-center gap-2">
            <ShieldCheckIcon
              className={`h-5 w-5 ${
                isFreeEducation ? "text-emerald-600" : "text-blue-600"
              }`}
            />
            <p
              className={`text-sm font-bold ${
                isFreeEducation ? "text-emerald-800" : "text-blue-800"
              }`}
            >
              {isFreeEducation
                ? "Free Quality School Education"
                : "Private Fee-Paying School"}
            </p>
          </div>
          <p
            className={`mt-1 text-xs ${
              isFreeEducation ? "text-emerald-700" : "text-blue-700"
            }`}
          >
            {isFreeEducation
              ? "Student tuition is SLE 0.00. Class-fee setup and student tuition-payment recording are disabled."
              : "Student fees can be set by class, academic year and term, and verified bank payments can be recorded."}
          </p>
        </div>
      </div>

      {isFreeEducation ? (
        <div className="rounded-2xl border border-emerald-200 bg-white p-8 text-center sm:p-12">
          <ShieldCheckIcon className="mx-auto h-12 w-12 text-emerald-500" />
          <h2 className="mt-3 text-lg font-bold text-slate-900">
            Student Tuition Is Free
          </h2>
          <p className="mx-auto mt-2 max-w-xl text-sm leading-relaxed text-slate-500">
            {SCHOOL_TYPE_LABELS[policy.school_type] || "This school"} is
            registered under Free Education. PawaSchool will not create tuition
            debts, outstanding school-fee balances or student tuition payments.
          </p>
          <div className="mx-auto mt-5 max-w-sm rounded-xl bg-emerald-50 px-5 py-4">
            <p className="text-xs font-medium text-emerald-700">
              Student Tuition
            </p>
            <p className="mt-1 text-2xl font-bold text-emerald-800">
              SLE 0.00
            </p>
          </div>
        </div>
      ) : (
        <>
          <form
            onSubmit={handleSubmit}
            className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6"
          >
            <div className="mb-5 flex items-center gap-2">
              <CurrencyDollarIcon className="h-5 w-5 text-primary-600" />
              <h2 className="text-sm font-bold text-slate-900">
                Class Fee Details
              </h2>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <label className="text-xs font-medium text-slate-600">
                Academic Year
                <select
                  name="academic_year_id"
                  value={form.academic_year_id}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-primary-400"
                >
                  <option value="">Select year</option>
                  {years.map((year) => (
                    <option key={year.id} value={year.id}>
                      {year.name || year.year}
                    </option>
                  ))}
                </select>
              </label>

              <label className="text-xs font-medium text-slate-600">
                Academic Term
                <select
                  name="academic_term_id"
                  value={form.academic_term_id}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-primary-400"
                >
                  <option value="">Select term</option>
                  {visibleTerms.map((term) => (
                    <option key={term.id} value={term.id}>
                      {term.name ||
                        term.short_name ||
                        `Term ${term.term_number}`}
                    </option>
                  ))}
                </select>
              </label>

              <label className="text-xs font-medium text-slate-600">
                Class
                <select
                  name="class_id"
                  value={form.class_id}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-primary-400"
                >
                  <option value="">Select class</option>
                  {classes.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="text-xs font-medium text-slate-600">
                Fee Amount (SLE)
                <input
                  name="amount"
                  type="text"
                  inputMode="decimal"
                  value={form.amount}
                  onChange={handleChange}
                  placeholder="1500.00"
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-primary-400"
                />
              </label>

              <label className="text-xs font-medium text-slate-600">
                Due Date{" "}
                <span className="text-slate-400">(optional)</span>
                <input
                  name="due_date"
                  type="date"
                  value={form.due_date}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-primary-400"
                />
              </label>

              <label className="text-xs font-medium text-slate-600 sm:col-span-2 lg:col-span-3">
                Notes <span className="text-slate-400">(optional)</span>
                <input
                  name="notes"
                  value={form.notes}
                  onChange={handleChange}
                  placeholder="e.g. Term 1 school fee"
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-primary-400"
                />
              </label>
            </div>

            <div className="mt-5 flex justify-end">
              <button
                disabled={saving}
                className="rounded-lg bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-60"
              >
                {saving ? "Saving..." : "Save Class Fee"}
              </button>
            </div>
          </form>

          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
            <div className="border-b border-slate-100 px-5 py-4">
              <h2 className="text-sm font-bold text-slate-900">
                Configured Fees
              </h2>
              <p className="text-xs text-slate-400">
                One fee per class, academic year and term
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-5 py-3">Class</th>
                    <th className="px-5 py-3">Year</th>
                    <th className="px-5 py-3">Term</th>
                    <th className="px-5 py-3">Amount</th>
                    <th className="px-5 py-3">Due</th>
                    <th className="px-5 py-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {fees.map((fee) => (
                    <tr key={fee.id} className="hover:bg-slate-50">
                      <td className="px-5 py-3 font-medium text-slate-800">
                        {fee.class?.name || `Class #${fee.class_id}`}
                      </td>
                      <td className="px-5 py-3 text-slate-500">
                        {fee.academicYear?.name ||
                          fee.academicYear?.year ||
                          "—"}
                      </td>
                      <td className="px-5 py-3 text-slate-500">
                        {fee.academicTerm?.name ||
                          fee.academicTerm?.short_name ||
                          "—"}
                      </td>
                      <td className="px-5 py-3 font-semibold text-primary-700">
                        {sle(fee.amount)}
                      </td>
                      <td className="px-5 py-3 text-slate-500">
                        {fee.due_date || "—"}
                      </td>
                      <td className="px-5 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => editFee(fee)}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-primary-600 hover:text-primary-700"
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}

                  {!fees.length && (
                    <tr>
                      <td
                        colSpan="6"
                        className="px-5 py-10 text-center text-sm text-slate-400"
                      >
                        No class fees configured yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}